import { useState, useEffect, useRef, useCallback } from 'react';

const CODE_CHARS = '01!@#$%{}[]<>/\\;:=+-~|^ABCDEF';

function scrambleChar() {
  return CODE_CHARS[Math.floor(Math.random() * CODE_CHARS.length)];
}

export function ScrambleText({ text, className, as: Tag = 'span', delay = 0, duration = 800, distortOnHover = false }) {
  const [output, setOutput] = useState('');
  const [hasTriggered, setHasTriggered] = useState(false);
  const [isRevealed, setIsRevealed] = useState(false);
  const ref = useRef(null);
  const frameRef = useRef(null);
  const hoverTimeoutRef = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasTriggered(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!hasTriggered) {
      setOutput(text.replace(/[^ ]/g, '\u00A0'));
      return;
    }

    const startTime = performance.now() + delay;
    const chars = text.split('');

    const animate = (now) => {
      const elapsed = now - startTime;

      if (elapsed < 0) {
        setOutput(chars.map(c => c === ' ' ? ' ' : scrambleChar()).join(''));
        frameRef.current = requestAnimationFrame(animate);
        return;
      }

      const progress = Math.min(elapsed / duration, 1);
      const revealedCount = Math.floor(progress * chars.length);

      const result = chars.map((char, i) => {
        if (i < revealedCount) return char;
        if (char === ' ') return ' ';
        return scrambleChar();
      }).join('');

      setOutput(result);

      if (progress < 1) {
        frameRef.current = requestAnimationFrame(animate);
      } else {
        setIsRevealed(true);
      }
    };

    frameRef.current = requestAnimationFrame(animate);
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [hasTriggered, text, delay, duration]);

  const handleMouseEnter = useCallback(() => {
    if (!distortOnHover || !isRevealed) return;

    let frame = 0;
    const totalFrames = 12;
    const chars = text.split('');

    const glitch = () => {
      frame++;
      const result = chars.map((char) => {
        if (char === ' ') return ' ';
        if (frame > totalFrames * 0.5 && Math.random() > 0.4) return char;
        if (Math.random() > 0.35) return char;
        return scrambleChar();
      }).join('');
      setOutput(result);

      if (frame < totalFrames) {
        hoverTimeoutRef.current = setTimeout(glitch, 30);
      } else {
        setOutput(text);
      }
    };

    glitch();
  }, [distortOnHover, isRevealed, text]);

  const handleMouseLeave = useCallback(() => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    if (isRevealed) setOutput(text);
  }, [isRevealed, text]);

  return (
    <Tag
      ref={ref}
      className={className}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={distortOnHover ? { cursor: 'default' } : undefined}
    >
      {output || '\u00A0'}
    </Tag>
  );
}
